import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  OnInit,
  signal,
  OnDestroy
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { RouterModule } from "@angular/router";
import { Store } from "@viewer/core";
import { SharedModule } from "@viewer/shared-module/shared.module";
import { IReactionDisposer, reaction, runInAction } from "mobx";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { CommentImportExportService } from "@viewer/toc-items/comments-module/services/comment-import-export.service";
import { Comment, DdnData } from "@orion2/models/tocitem.models";
import { TitleService } from "@viewer/header/title.service";
import { SelectionModel } from "@angular/cdk/collections";
import { CommentComponent } from "@viewer/toc-items/comments-module/comment/comment.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult
} from "libs/components/confirm-dialog/confirm-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "@orion2/message-service/message.service";
import { decompressFile } from "@orion2/utils/front.utils";
import { CUSTOM_FILE_ERRORS, DDN_ERRORS, GENERAL_ERRORS } from "@orion2/utils/errors.utils";
import { DdnModalComponent } from "@viewer/toc-items/comments-module/ddn-modal/ddn-modal.component";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { TocItemsModule } from "@viewer/toc-items/toc-items.module";
import { CommentStatus, CommentTypes } from "@viewer/shared-module/constant";
import { getCompactedDMC } from "@orion2/utils/functions.utils";
import { OBJECT_SEPARATOR } from "@orion2/utils/datamodule.utils";
import { HistorySource } from "@orion2/models/enums";
import { HistoricDirective } from "@viewer/shared-module/historic.directive";

@Component({
  standalone: true,
  selector: "o-comment-list",
  imports: [
    MatIconModule,
    SharedModule,
    MatButtonModule,
    MatMenuModule,
    RouterModule,
    CommentComponent,
    MatCheckboxModule,
    MatTableModule,
    TocItemsModule,
    HistoricDirective
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./comment-list.component.html",
  styleUrl: "./comment-list.component.scss"
})
export class CommentListComponent implements OnInit, OnDestroy {
  public expanded = false;
  public dataSource = new MatTableDataSource<Comment>([]);
  public displayedColumns: string[];
  public selectionModel = new SelectionModel<string>(true, []);
  public selectMode = signal(false);
  public itemType = "comment";
  public historySource = HistorySource;
  private itemPerPageDisposer: IReactionDisposer;

  public readonly canManageAtLeastOneComment = computed(() =>
    Array.from(this.commentService.allComments()).some((comment: Comment) =>
      this.commentService.canManage(comment)
    )
  );

  /*
    The comments must be stringified to be able to be filtered (codes are
    converted to strings).
    Check if the filtered value is in a stringified comment.
  */
  private readonly commentFilterPredicate = (comment: Comment, filter: string): boolean =>
    this.stringifyComment(comment).includes(filter.trim().toLowerCase());

  constructor(
    public readonly commentService: CommentService,
    public readonly store: Store,
    private readonly titleService: TitleService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly commentImportExportService: CommentImportExportService,
    private readonly messageService: MessageService
  ) {
    effect(() => {
      this.commentService.allComments();
      if (!store.isGov) {
        return;
      }
      this.updateDataSource();
    });
  }

  ngOnInit(): void {
    this.titleService.setPageTitle("comments.label");
    this.displayedColumns = [this.itemType];

    this.dataSource.filterPredicate = this.commentFilterPredicate;

    // unselect card on list and force combineLatest (toc.component)
    runInAction(() => {
      this.store.currentTocNode = {
        ...this.store.currentTocNode,
        unselected: true
      };
      // Need to reset currentDMC in case of comming from DMC to comment-list
      // if create a comment with APP/PUB location
      // the comment created is visible in this DMC
      this.store.currentDMC = undefined;
    });
    // Update the number of comment display on screen after setting
    this.itemPerPageDisposer = reaction(
      () => this.store.userItemsPerPage,
      (value: number) => {
        this.dataSource.paginator._changePageSize(value);
      }
    );
  }

  ngOnDestroy(): void {
    this.itemPerPageDisposer();
  }

  /**
   * Update dataSource to replicate changes to the MatTable.
   * @param comments New list of comments. Overrides the previous list of comments of the component.
   * @returns {void}
   */
  public updateDataSource(): void {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<Comment>();
    }
    this.dataSource.data = this.commentService.allComments();
  }

  /**
   * Allows you to expand or reduce all comments.
   * @public
   * @returns {void}
   */
  public toggleExpansionAll(): void {
    this.expanded = !this.expanded;
  }

  /**
   * Allows you to select or deselect comment by id.
   * @public
   * @param {string} commentId
   * @returns {void}
   */
  public toggleSelection(commentId: string): void {
    this.selectionModel.toggle(commentId);
    this.selectMode.set(this.selectionModel.hasValue());
  }

  /**
   * Select or deselect all comments. The select all is applied on filtered comments.
   * @public
   * @returns {void}
   */
  public toggleAllSelection(): void {
    this.selectMode.set(!this.selectMode());
    this.selectMode()
      ? this.dataSource.filteredData.forEach(comment => {
          if (this.commentService.canManage(comment)) {
            this.selectionModel.select(comment._id);
          }
        })
      : this.selectionModel.clear();
  }

  /**
   * Call generic deletion function to delete all comments.
   * @public
   * @returns {void}
   */
  public deleteAll(): void {
    this.deleteConfirmDialog(this.commentService.allComments(), "comments.delete.all.confirm");
  }

  /**
   * Call generic deletion function to delete selected comments.
   * @public
   * @returns {void}
   */
  public deleteSelected(): void {
    this.deleteConfirmDialog(this.getSelection(), "comments.delete.selected.confirm");
  }

  /**
   * Exports the selection of comments.
   * The selection is exported inside a ZIP file, with each comment being an XML file.
   * @param {DdnData} ddnData data added to the zip containing sender and receiver informations
   * @public
   * @returns {void}
   */
  public exportSelection(ddnData?: DdnData): void {
    try {
      this.commentImportExportService.exportSelection(this.getSelection(), ddnData);
    } catch (error) {
      this.messageService.error(this.translate.instant(error));
    }
  }

  /**
   * Open modal for getting DDN Data and call exportSelection
   * @public
   * @returns {void}
   */
  public exportSelectionWithDDN(): void {
    this.dialog
      .open(DdnModalComponent, {
        panelClass: "ddn-modal-container"
      })
      .afterClosed()
      .subscribe((ddnData: DdnData) => {
        if (ddnData) {
          this.exportSelection(ddnData);
        }
      });
  }

  /**
   * Imports comments from a ZIP file.
   *
   * It then displays a modal summarising the import (errors, warnings, and
   * successes).
   *
   * @param {Event} event The event from which to get the ZIP file to download.
   * @returns {Promise<void>} Nothing.
   */
  public async importComments(event: Event): Promise<void> {
    if (!this.store.offlineSearchReady) {
      return;
    }

    try {
      const inputFile = (event.target as HTMLInputElement).files[0];
      (event.target as HTMLInputElement).value = "";

      const unzip = await decompressFile(inputFile);
      await this.commentImportExportService.importComments(unzip);
    } catch (error) {
      let message: string;
      switch (error.message) {
        case CUSTOM_FILE_ERRORS.ZIP_ERROR:
          message = "error.file.format";
          break;
        case CUSTOM_FILE_ERRORS.XML_ERROR:
          message = "comments.import.error.zip";
          break;
        case DDN_ERRORS.MULTIPLE_DDN:
          message = "comments.import.error.ddn.multiple";
          break;
        case DDN_ERRORS.INVALID_STRUCTURE:
          message = "comments.import.error.ddn.structure";
          break;
        default:
          message = GENERAL_ERRORS.TECHNICAL;
          break;
      }
      this.messageService.error(this.translate.instant(message), undefined, 4000);
    }
  }

  /**
   * Retrieves the list of the currently selected comments.
   * @public
   * @returns {Comment[]} Selected comments.
   */
  private getSelection(): Comment[] {
    return this.commentService
      .allComments()
      ?.filter((comment: Comment) => this.selectionModel.isSelected(comment._id));
  }

  /**
   * Opens a confirmation modal with the text given in parameters.
   * After confirmation, allows the user to delete the given comments.
   * @private
   * @param {Comment[]} comments The comments to delete.
   * @param {string} confirmText The translation key of the message to display in each case.
   * @returns {void}
   */
  private deleteConfirmDialog(comments: Comment[], confirmText: string) {
    const data: ConfirmDialogData = {
      message: [this.translate.instant(confirmText)],
      confirmLabels: [this.translate.instant("continue")],
      cancelLabels: [this.translate.instant("cancel")]
    };

    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe((result: ConfirmDialogResult) => {
        if (result.confirm) {
          this.commentService.deleteComments(comments);
        }
      });
  }

  /**
   * Transforms a comment in string with attributes to be taken into account for comparison. The attributes
   * in code are transformed with the corresponding string (priority, commentType...).
   * @param {Comment} comment The comment to be stringified.
   * @returns {string} The stringified comment with attributes to be taken into account for comparison.
   */
  private stringifyComment(comment: Comment): string {
    const stringifiedComment = [
      comment.author,
      comment.code,
      comment.contents,
      this.translate.instant("comments.priority." + comment.priority),
      comment.reference,
      CommentTypes[comment.commentType] ?? CommentTypes["default"],
      comment.title,
      comment.revision,
      CommentStatus[comment.responseType] ?? CommentStatus["default"]
    ];

    if (comment.dmInfos) {
      stringifiedComment.push(
        comment.dmInfos?.dmIssueInfos,
        comment.dmInfos?.dmTitle,
        getCompactedDMC(comment.dmInfos?.dmc)
      );
    }
    if (comment.pmInfos) {
      stringifiedComment.push(comment.pmInfos.pmCode);
    }
    if (comment.pubInfos) {
      stringifiedComment.push(comment.pubInfos.pubCode, comment.pubInfos.verbatimText);
    }
    if (comment.appInfos) {
      stringifiedComment.push(comment.appInfos.appName, comment.appInfos.appVersion);
    }

    return stringifiedComment.flat().join(OBJECT_SEPARATOR).toLowerCase();
  }
}
